import { Box, Button, Grid, Typography } from '@mui/material';
import { MaterialPatternString } from 'Pattern';
import React from 'react';
import { BoardType, ParamsPanelProps, RenderableMetadata } from 'Types';

import MaterialPatternInput from 'designer/MaterialPatternInput';
import NumberInput from 'designer/NumberInput';
import SubpatternInput from 'designer/PatternInput';
import { useSelectedBoardInstance } from 'utils/hooks';
import { subpatternIndexToKey } from 'utils/patternUtils';

function StripsAdvancedParamsPanel(
  props: ParamsPanelProps<BoardType.StripsAdvanced>
) {
  const { params, selection, onParamChange, onSelectionChange, units } = props;
  const selectedBoard = useSelectedBoardInstance();

  const onSubpatternParamChange: (
    patternString: MaterialPatternString,
    index: number
  ) => void = (patternString: MaterialPatternString, index: number) => {
    if (selectedBoard == null) {
      return;
    }

    const newSubpatterns = [...params.subpatterns];
    newSubpatterns[index] = patternString;

    onParamChange('subpatterns')(newSubpatterns);
  };

  const addSubpattern = () => {
    if (selectedBoard == null) {
      return;
    }

    onParamChange('subpatterns')(
      params.subpatterns.concat('' as MaterialPatternString)
    );
  };

  const removeSubpattern = (index: number) => {
    if (selectedBoard == null) {
      return;
    }

    const newSubpatterns = [...params.subpatterns];
    newSubpatterns.splice(index, 1);

    onParamChange('subpatterns')(newSubpatterns);
  };

  const onSelectionChangeWrapped = (
    selection: null | RenderableMetadata<BoardType.Chevron>,
    subpatternIndex: number
  ) => {
    onSelectionChange(
      selection == null
        ? null
        : { renderableIndex: selection.fragmentIndex, subpatternIndex }
    );
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid container item spacing={2}>
        <Grid item>
          <NumberInput
            id="thickness"
            label="Thickness"
            step={1 / 8}
            value={params.thickness}
            onChange={onParamChange('thickness')}
          />
        </Grid>
      </Grid>

      <Grid item>
        <SubpatternInput
          id="pattern"
          onChange={onParamChange('pattern')}
          value={params.pattern}
        ></SubpatternInput>
      </Grid>

      <Grid item>
        <Box sx={{ pb: 2 }}>
          <Typography>Subpatterns</Typography>
        </Box>

        <Grid container direction="column" spacing={4}>
          {params.subpatterns.map((subpattern, index) => (
            <Grid item key={index}>
              <MaterialPatternInput
                id={`subpattern-${subpatternIndexToKey(index)}`}
                label={`Subpattern ${subpatternIndexToKey(index)}`}
                onChange={(patternString) =>
                  onSubpatternParamChange(patternString, index)
                }
                onRemove={() => removeSubpattern(index)}
                onSelectionChange={(selection) =>
                  onSelectionChangeWrapped(selection, index)
                }
                previewHeight={1}
                selection={
                  selection?.subpatternIndex === index ? selection : null
                }
                units={units}
                value={subpattern}
              ></MaterialPatternInput>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid container item justifyContent="flex-end">
        <Button color="primary" onClick={addSubpattern}>
          Add Subpattern
        </Button>
      </Grid>
    </Grid>
  );
}

export default StripsAdvancedParamsPanel;
